import { motion } from "framer-motion";
import React, { useEffect, useMemo, useState } from "react";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import { shallowEqual } from "react-redux";
import TroopBox from "../../components/Boxes/TroopBox";
import Button from "../../components/Button";
import { CustomIcon } from "../../components/Icons";
import ItemSection from "../../components/ItemSection";
import ModifiedStat from "../../components/ModifiedStat";
import ViewTypeSwitcher from "../../components/ViewTypeSwitcher";
import { Assets } from "../../constants/assets";
import useCamp from "../../game-engine/hooks/useCamp";
import { MoveTroopType } from "../../game-engine/types/enum";
import { useAppDispatch, useAppSelector } from "../../hooks/reduxHook";
import useToast from "../../hooks/useToast";
import {
  fetchBarracksBuildingInfo,
  fetchGetMe,
  fetchPlayerTroop,
} from "../../redux/pages/barracks/barracksActions";
import { BarracksState } from "../../redux/pages/barracks/barracksSlice";
import { setUserInfo } from "../../redux/user/action";
import { ItemBonusStatEnum, PlayerItemType } from "../../types/items";
import { PlayerTroopType } from "../../types/troop";
import Network from "../../utils/Network";
import { roundFloat } from "../../utils/round-float";
import {
  getStatLine,
  originalBarracksTotal,
  StatModifiers,
} from "../../utils/statModifiers";
import ThousandsSeparator from "../../utils/thousands-separator";
import { useBarracksUIContext } from "./BarracksContext";
import DeleteDialog from "./BarracksDialogs/DeleteDialog";
import Camp from "../../game-engine/components/camp";
import { useAppTranslation } from "../../i18n/useAppTranslation";

const BarracksPage = () => {
  const openToast = useToast();
  const axios = new Network();
  const { moveTroop } = useCamp();
  const { t } = useAppTranslation(["barracks"]);

  const dispatch = useAppDispatch();

  const [viewType, setViewType] = useState(
    +localStorage.getItem("card-sort")! || 1
  );
  const [instantLoading, setInstantLoading] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean;
    id: number;
    reward: string;
  }>({ open: false, id: 0, reward: "" });
  const barracksContext = useBarracksUIContext();
  const barracksUIProps = useMemo(() => {
    return {
      setConfirmDialog: barracksContext?.setConfirmDialog,
      setTrainDialog: barracksContext?.setTrainDialog,
      upgradeLoading: barracksContext?.upgradeLoading,
      selectedTab: barracksContext?.selectedTab,
      setUpgradeLoading: barracksContext?.setUpgradeLoading,
      setSellDialog: barracksContext?.setSellDialog,
      setMergeDialog: barracksContext?.setMergeDialog,
      setUpgradeDialog: barracksContext?.setUpgradeDialog,
      setTroopItemsDialog: barracksContext?.setTroopItemsDialog,
      setInstantHealDialog: barracksContext?.setInstantHealDialog,
    };
  }, [
    barracksContext?.setConfirmDialog,
    barracksContext?.setTrainDialog,
    barracksContext?.upgradeLoading,
    barracksContext?.selectedTab,
    barracksContext?.setUpgradeLoading,
    barracksContext?.setSellDialog,
    barracksContext?.setMergeDialog,
    barracksContext?.setUpgradeDialog,
    barracksContext?.setTroopItemsDialog,
    barracksContext?.setInstantHealDialog,
  ]);

  const { playerTroopList, barrackBuildingInfo, isLoading }: BarracksState =
    useAppSelector(({ barracks }) => {
      return {
        playerTroopList: barracks.playerTroopList,
        barrackBuildingInfo: barracks.barrackBuildingInfo,
        isLoading: barracks.isLoading,
      };
    }, shallowEqual) as BarracksState;

  const plyerTroopFetch = () => {
    dispatch(fetchPlayerTroop(50));
  };

  function getDetailedStat(
    items: PlayerItemType[],
    value,
    bonusStat: ItemBonusStatEnum,
    statModifiers?: StatModifiers
  ) {
    // While an event modifier runs, the troop field is the original and the stat line holds the value in use.
    const originalTotal = originalBarracksTotal(
      items,
      statModifiers,
      value,
      bonusStat,
      barrackBuildingInfo?.bonusStats
    );
    const statLine = getStatLine(statModifiers, bonusStat);
    if (statLine) value = statLine.value;
    const bonusValue = !barrackBuildingInfo?.bonusStats
      ? 0
      : (value * barrackBuildingInfo?.bonusStats) / 100;
    let itemValue = 0;
    const relatedItem = items?.filter(
      (item) => item.item.bonusStat === bonusStat
    );
    if (relatedItem && relatedItem?.length > 0) {
      for (let key in relatedItem) {
        itemValue += relatedItem[key].bonusValue;
      }
    }
    return {
      value: ThousandsSeparator(value),
      bonusValue: bonusValue,
      itemValue: itemValue,
      total: ThousandsSeparator(value + bonusValue + itemValue),
      originalTotal:
        originalTotal === undefined
          ? undefined
          : ThousandsSeparator(originalTotal),
    };
  }

  function openSellModal(item: PlayerTroopType) {
    barracksUIProps?.setSellDialog?.({
      open: true,
      sellPrice: 0,
      item,
    });
  }

  function handleOpenItemDialog(troop: PlayerTroopType) {
    barracksUIProps?.setTroopItemsDialog?.({
      open: true,
      troop,
    });
  }

  const burnTroop = async (troopId: number) => {
    barracksUIProps.setUpgradeLoading?.(true);
    const response = await axios.CallApi({
      url: `/playertroop/burn`,
      method: "post",
      body: {
        troopId,
      },
    });
    openToast(response);
    if (response.ok) {
      setDeleteDialog((prev) => ({ ...prev, open: false }));
      plyerTroopFetch();
    }
    barracksUIProps.setUpgradeLoading?.(false);
  };

  const getUpgradeCost = async (id: number) => {
    barracksUIProps?.setUpgradeLoading?.(true);
    const response = await axios.CallApi({
      url: `/playertroop/${id}/upgrade/direct/cost`,
      method: "get",
    });
    barracksUIProps?.setUpgradeLoading?.(false);
    barracksUIProps?.setUpgradeDialog?.({
      cost: response.data,
      open: true,
      id,
    });
  };

  const handleOpenBurnDialog = (id: number, reward?: number) => {
    setDeleteDialog({ open: true, id: id, reward: `${reward}` });
  };

  const handleCancelSellOrder = async (id: number) => {
    barracksUIProps?.setUpgradeLoading?.(true);
    const response = await axios.CallApi({
      url: "/market/my-order/" + id,
      method: "delete",
    });
    openToast(response);
    if (response.ok) {
      barracksUIProps?.setConfirmDialog?.((prev) => ({ ...prev, open: false }));
      plyerTroopFetch();
    }
    barracksUIProps?.setUpgradeLoading?.(false);
  };

  const handleInstantUpgrade = async (troopId) => {
    barracksUIProps?.setUpgradeLoading?.(true);
    const response = await axios.CallApi({
      url: "/playertroop/upgrade/instant",
      body: {
        troopId,
      },
    });
    openToast(response);
    if (response.ok) {
      plyerTroopFetch();
      dispatch(fetchBarracksBuildingInfo());
      barracksUIProps?.setConfirmDialog?.((prev) => ({
        ...prev,
        open: false,
      }));
    }
    barracksUIProps?.setUpgradeLoading?.(false);
    dispatch(fetchGetMe()).then((response) => {
      if (response?.ok) {
        dispatch(setUserInfo(response?.data));
      }
    });
  };

  async function openInstantUpgradeModal(
    id: number,
    cost: number,
    reward: number
  ) {
    setInstantLoading(!!id);
    const response = await axios.CallApi({
      url: `/playertroop/${id}/upgrade/instant/cost`,
      method: "get",
    });
    setInstantLoading(false);
    barracksUIProps?.setConfirmDialog?.({
      open: true,
      submit: () => handleInstantUpgrade(id),
      title: t("barracks:dialogs.instantUpgrade.title"),
      msg: t("barracks:dialogs.instantUpgrade.confirm", {
        cost: response?.data?.cost || 0,
        reward,
      }),
    });
  }

  useEffect(() => {
    plyerTroopFetch();
    dispatch(fetchGetMe());
  }, []);

  return (
    <>
      {barracksUIProps?.selectedTab !== "items" &&
        barracksUIProps?.selectedTab !== "camp" && (
          <ViewTypeSwitcher
            viewType={viewType}
            setViewType={setViewType}
            className="col-12 d-flex align-items-center justify-content-end mt-2 mt-md-0 mb-0 mb-md-3"
          />
        )}
      {!barracksUIProps?.selectedTab && (
        <motion.div
          exit={{ opacity: 0, y: 200 }}
          transition={{ duration: 0.4, delay: 0.1 }}
          initial={isMobile ? undefined : { opacity: 0, y: 200 }}
          animate={{ opacity: 1, y: 0 }}
          className="row mt-3 mt-md-0"
        >
          {isLoading &&
            [...Array(8)]?.map((item, index) => (
              <div className="col-sm-6 col-12 col-lg-4 col-xl-3" key={index}>
                <TroopBox key={item} loading={isLoading} />
              </div>
            ))}

          {!isLoading &&
            barrackBuildingInfo &&
            barrackBuildingInfo?.currentCapacity ===
              barrackBuildingInfo?.thisLevelCapacity && (
              <div className="mt-2 alert-danger text-center alert">
                {t("barracks:alerts.capacityFull")}
              </div>
            )}
          {!isLoading && playerTroopList?.length === 0 && (
            <div className="mt-5 pt-5 text-secondary text-center">
              {t("barracks:empty.noTroops")}{" "}
              <a
                className="text-info text-decoration-underline pointer"
                onClick={() => barracksUIProps?.setTrainDialog?.(true)}
              >
                {t("barracks:empty.trainCta")}
              </a>
            </div>
          )}
          {!isLoading &&
            playerTroopList?.map((troop: PlayerTroopType, index: number) => {
              const damageStats = getDetailedStat(
                troop.items!,
                troop.damage,
                ItemBonusStatEnum.Damage,
                troop.statModifiers
              );
              const armorStats = getDetailedStat(
                troop.items!,
                troop.armor,
                ItemBonusStatEnum.Armor,
                troop.statModifiers
              );
              const magicStats = getDetailedStat(
                troop.items!,
                troop.magicResistance,
                ItemBonusStatEnum.MagicResistance,
                troop.statModifiers
              );
              const rangeStats = getDetailedStat(
                troop.items!,
                troop.range,
                ItemBonusStatEnum.Range
              );
              const healthStats = getDetailedStat(
                troop.items!,
                troop.health,
                ItemBonusStatEnum.Health
              );
              let discountPercentage: boolean | string | number = false;
              if (
                troop.clegUpgradeCost &&
                troop.clegLastUpgradeCost &&
                troop.clegUpgradeCost !== troop.clegLastUpgradeCost
              ) {
                discountPercentage = roundFloat(
                  100 -
                    (troop.clegLastUpgradeCost * 100) / troop.clegUpgradeCost,
                  0,
                  2
                );
              }

              return (
                <div
                  className="col-sm-6 col-12 col-lg-4 col-xl-3"
                  key={troop?.id ?? index}
                >
                  <TroopBox
                    type={viewType}
                    // leverage={troop.troopId===7?1.5:undefined}
                    refresh={() => plyerTroopFetch()}
                    troop={troop?.troop}
                    {...troop}
                    bonus={barrackBuildingInfo?.bonusStats}
                    damage={
                      <div>
                        <ModifiedStat
                          value={damageStats.total}
                          original={damageStats.originalTotal}
                        />
                        <OverlayTrigger
                          trigger={"click"}
                          rootClose
                          overlay={
                            <Tooltip className="fs-tiny">
                              <b>{damageStats.value}</b>{" "}
                              {t("barracks:stats.damage")}
                              {+damageStats.bonusValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +
                                    {ThousandsSeparator(roundFloat(damageStats.bonusValue))}
                                  </b>{" "}
                                  {t("barracks:stats.bonus")}
                                </>
                              )}
                              {+damageStats.itemValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(roundFloat(damageStats.itemValue))}
                                  </b>{" "}
                                  {t("barracks:stats.itemDamage")}
                                </>
                              )}
                            </Tooltip>
                          }
                        >
                          <span>
                            <CustomIcon
                              style={{ width: 17, height: 17, marginBottom: 1 }}
                              className="pointer ms-1 "
                              icon="info"
                            />
                          </span>
                        </OverlayTrigger>
                      </div>
                    }
                    healthBonus={healthStats.itemValue}
                    armor={
                      <>
                        <ModifiedStat
                          value={armorStats.total}
                          original={armorStats.originalTotal}
                        />
                        <OverlayTrigger
                          trigger={"click"}
                          rootClose
                          overlay={
                            <Tooltip className="fs-tiny">
                              <b>{armorStats.value}</b>{" "}
                              {t("barracks:stats.armor")}
                              {+armorStats.bonusValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(roundFloat(armorStats.bonusValue))}
                                  </b>{" "}
                                  {t("barracks:stats.bonus")}
                                </>
                              )}
                              {+armorStats.itemValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(roundFloat(armorStats.itemValue))}
                                  </b>{" "}
                                  {t("barracks:stats.itemArmor")}
                                </>
                              )}
                            </Tooltip>
                          }
                        >
                          <span>
                            <CustomIcon
                              style={{ width: 17, height: 17, marginBottom: 1 }}
                              className="pointer ms-1 "
                              icon="info"
                            />
                          </span>
                        </OverlayTrigger>
                      </>
                    }
                    magicResistance={
                      <>
                        <ModifiedStat
                          value={magicStats.total}
                          original={magicStats.originalTotal}
                        />
                        <OverlayTrigger
                          trigger={"click"}
                          rootClose
                          overlay={
                            <Tooltip className="fs-tiny">
                              <b>{magicStats.value}</b>{" "}
                              {t("barracks:stats.magicResistance")}
                              {+magicStats.bonusValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(roundFloat(magicStats.bonusValue))}
                                  </b>{" "}
                                  {t("barracks:stats.bonus")}
                                </>
                              )}
                              {+magicStats.itemValue > 0 && (
                                <>
                                  <b className="text-info">
                                    +{ThousandsSeparator(roundFloat(magicStats.itemValue))}
                                  </b>{" "}
                                  {t("barracks:stats.itemMagicResistance")}
                                </>
                              )}
                            </Tooltip>
                          }
                        >
                          <span>
                            <CustomIcon
                              style={{ width: 17, height: 17, marginBottom: 1 }}
                              className="pointer ms-1 "
                              icon="info"
                            />
                          </span>
                        </OverlayTrigger>
                      </>
                    }
                    range={
                      <>
                        {troop.range}
                        {rangeStats.itemValue > 0 && (
                          <span className="text-info">
                            +{rangeStats.itemValue}
                          </span>
                        )}
                      </>
                    }
                    abilities={troop?.abilities && JSON.parse(troop?.abilities)}
                    action={
                      <>
                        {viewType === 2 && (
                          <div className="dark-yellow text-shadow">
                            {t("barracks:labels.xp")} {troop.xp}/{troop.maxXp}
                          </div>
                        )}
                        {troop?.state === 6 && (
                          <Button
                            onClick={() => {
                              barracksUIProps?.setConfirmDialog?.({
                                open: true,
                                submit: () =>
                                  handleCancelSellOrder(troop.marketOrderId),
                                title: t("barracks:labels.cancelOrder"),
                                msg: (
                                  <>
                                    {t("barracks:messages.cancelSellOrderConfirm")}
                                  </>
                                ),
                              });
                            }}
                            className="w-100"
                            variant="wide"
                          >
                            {t("barracks:labels.cancelOrder")}
                          </Button>
                        )}
                        {(troop.state === 2 || troop.state === 3) && (
                          <Button
                            style={{
                              width: "90%",
                              backgroundSize: "100% 100%",
                            }}
                            loading={instantLoading === troop?.id}
                            disabled={
                              troop?.state !== 1 &&
                              troop?.state !== 3 &&
                              troop?.state !== 2
                            }
                            onClick={() => {
                              openInstantUpgradeModal(
                                troop.id,
                                troop.instantUpgradeCost,
                                troop.instantUpgradeEmberReward
                              );
                            }}
                            variant={"wide-primary"}
                          >
                            {t("barracks:labels.instantAction", {
                              action:
                                troop.state === 2
                                  ? t("barracks:labels.train")
                                  : t("barracks:labels.upgrade"),
                            })}{" "}
                            {troop.state === 3 &&
                              troop.instantUpgradeEmberReward &&
                              troop.instantUpgradeEmberReward > 0 && (
                                <span className="fs-tiny ms-1">
                                  (+{troop.instantUpgradeEmberReward}
                                  {" "}<img
                                    src={Assets.icons.ember}
                                    width={15}
                                    height={15}
                                    alt="ember"
                                  />
                                  )
                                </span>
                              )}
                          </Button>
                        )}
                        {(troop.state === 1 || troop.state === 4 || troop.state === 9) && (
                          <OverlayTrigger
                            rootClose
                            trigger="click"
                            placement="top"
                            overlay={
                              <div className="overlay-actions text-white">
                                <motion.div
                                  initial={{
                                    y: 100,
                                    opacity: 0,
                                  }}
                                  animate={{ y: 0, opacity: 1 }}
                                  transition={{ delay: 0 }}
                                  onClick={() => openSellModal(troop)}
                                  className="d-flex px-2 py-1 justify-content-start pointer align-items-center"
                                >
                                  <img
                                    width={25}
                                    className="me-3"
                                    src="/assets/images/icons/offer.png"
                                    alt="offer"
                                  />{" "}
                                  {t("barracks:labels.sell")}
                                </motion.div>

                                <motion.div
                                  initial={{
                                    y: 100,
                                    opacity: 0,
                                  }}
                                  onClick={() =>
                                    barracksUIProps?.setMergeDialog?.({
                                      open: true,
                                      source: troop.id,
                                      dest: 0,
                                      sourceItem: troop,
                                    })
                                  }
                                  animate={{ y: 0, opacity: 1 }}
                                  transition={{ delay: 0.1 }}
                                  className="d-flex border-top-1 px-2 py-1 justify-content-start pointer align-items-center"
                                >
                                  <img
                                    width={25}
                                    className="me-3"
                                    src="/assets/images/icons/merge.png"
                                    alt="merge"
                                  />{" "}
                                  {t("barracks:labels.merge")}
                                </motion.div>

                                <motion.div
                                  initial={{
                                    y: 100,
                                    opacity: 0,
                                  }}
                                  onClick={() =>
                                    troop.troop.trainable
                                      ? getUpgradeCost(troop.id)
                                      : ""
                                  }
                                  animate={{
                                    y: 0,
                                    opacity: troop.troop.trainable ? 1 : 0.4,
                                  }}
                                  transition={{ delay: 0.2 }}
                                  className="d-flex px-2 py-1 justify-content-start pointer align-items-center"
                                >
                                  <img
                                    width={25}
                                    className="me-3"
                                    src="/assets/images/icons/upgrade.png"
                                    alt="upgrade"
                                  />{" "}
                                  {t("barracks:labels.upgrade")}{" "}
                                  {Boolean(troop.upgradeEmberReward) &&
                                    troop.upgradeEmberReward > 0 && (
                                      <div className="badge-fire ms-2 position-static">
                                        <div>{t("barracks:stats.earn")}</div>+
                                        {ThousandsSeparator(
                                          troop.upgradeEmberReward
                                        )}
                                        <img
                                          src={Assets.icons.ember}
                                          width={18}
                                          height={18}
                                        />
                                      </div>
                                    )}
                                  {discountPercentage && (
                                    <div
                                      style={{
                                        width: 35,
                                        height: 35,
                                        fontSize: 10,
                                        position: "relative",
                                      }}
                                      className="off-badge ms-2"
                                    >
                                      <div>
                                        <div className="dark-yellow fs-lg">
                                          {discountPercentage}%
                                        </div>
                                        <small className="dark-yellow">
                                          {t("barracks:stats.off")}
                                        </small>
                                      </div>
                                    </div>
                                  )}
                                </motion.div>
                                <motion.div
                                  initial={{
                                    y: 100,
                                    opacity: 0,
                                  }}
                                  animate={{ y: 0, opacity: 1 }}
                                  onClick={() => handleOpenItemDialog(troop)}
                                  transition={{ delay: 0.3 }}
                                  className="d-flex bt-1  px-2 py-1 justify-content-start pointer align-items-center"
                                >
                                  <img
                                    width={25}
                                    className="me-3"
                                    src="/assets/images/icons/items.png"
                                    alt=""
                                  />{" "}
                                  {t("barracks:labels.items")}
                                </motion.div>
                                {troop.health < troop.maxHealth && (
                                  <motion.div
                                    initial={{
                                      y: 100,
                                      opacity: 0,
                                    }}
                                    animate={{ y: 0, opacity: 1 }}
                                    onClick={() =>
                                      barracksUIProps?.setInstantHealDialog?.({
                                        open: true,
                                        troopId: troop.id,
                                        troop,
                                        targetType: "single",
                                      })
                                    }
                                    transition={{ delay: 0.35 }}
                                    className="d-flex bt-1 px-2 py-1 justify-content-start pointer align-items-center"
                                  >
                                    <img
                                      width={22}
                                      className="me-3"
                                      src="/assets/images/icons/icon-health-restore.png"
                                      alt="heal"
                                    />{" "}
                                    {t("barracks:labels.instantHeal")}
                                  </motion.div>
                                )}
                                <motion.div
                                  initial={{
                                    y: 100,
                                    opacity: 0,
                                  }}
                                  animate={{ y: 0, opacity: 1, height: 38 }}
                                  onClick={() =>
                                    barracksUIProps?.setConfirmDialog?.({
                                      open: true,
                                      submit: () =>
                                        moveTroop(
                                          troop.id,
                                          MoveTroopType.CAMP
                                        ).then((res) => {
                                          if (!res.ok) return;
                                          barracksUIProps?.setConfirmDialog?.(
                                            (prev) => ({
                                              ...prev,
                                              open: false,
                                            })
                                          );
                                          plyerTroopFetch();
                                        }),
                                      msg: t("barracks:messages.moveToCampConfirm", {
                                        troop: troop?.troop?.name,
                                      }),
                                      title: t("barracks:labels.areYouSure"),
                                    })
                                  }
                                  transition={{ delay: 0.4 }}
                                  className="d-flex bt-1  px-2 py-1 justify-content-start pointer align-items-center"
                                >
                                  <img
                                    width={20}
                                    className="me-3"
                                    src={Assets.icons.barracks}
                                    alt=""
                                  />{" "}
                                  {t("barracks:labels.moveToCamp")}
                                </motion.div>

                                <motion.div
                                  initial={{
                                    y: 100,
                                    opacity: 0,
                                  }}
                                  onClick={() =>
                                    handleOpenBurnDialog(
                                      troop.id,
                                      troop.burnEmberReward
                                    )
                                  }
                                  animate={{
                                    y: 0,
                                    opacity: 1,
                                  }}
                                  transition={{ delay: 0.5 }}
                                  className="d-flex px-2 py-1 justify-content-start pointer align-items-center"
                                >
                                  <img
                                    width={22}
                                    className="me-3"
                                    src="/assets/images/icons/trash.svg"
                                    alt="delete"
                                  />{" "}
                                  {t("barracks:dialogs.delete.delete")}
                                </motion.div>
                              </div>
                            }
                          >
                            <div
                              className={viewType === 2 ? "ms-auto" : "mx-auto"}
                            >
                              <Button
                                disabled={troop.state === 4}
                                className="ms-auto p-relative px-2 bg-100 pe-3"
                                variant="primary"
                                onClick={(e) =>
                                  troop.state === 4 && e.stopPropagation()
                                }
                              >
                                <img
                                  src="/assets/images/icons/menu.png"
                                  width={20}
                                  alt="menu"
                                />
                                {t("barracks:labels.options")}
                                {discountPercentage && (
                                  <div
                                    className="circle bg-danger"
                                    style={{
                                      minWidth: 8,
                                      minHeight: 8,
                                      position: "absolute",
                                      top: 4,
                                      right: 4,
                                    }}
                                  ></div>
                                )}
                                {Boolean(troop.upgradeEmberReward) &&
                                  troop.upgradeEmberReward > 0 && (
                                    <div
                                      className="circle bg-purple"
                                      style={{
                                        minWidth: 8,
                                        minHeight: 8,
                                        position: "absolute",
                                        top: "39%",
                                        right: 6,
                                      }}
                                    ></div>
                                  )}
                              </Button>
                            </div>
                          </OverlayTrigger>
                        )}
                      </>
                    }
                  />
                </div>
              );
            })}
        </motion.div>
      )}

      {barracksUIProps?.selectedTab === "items" && <ItemSection />}
      {barracksUIProps?.selectedTab === "camp" && <Camp />}

      <DeleteDialog
        open={deleteDialog.open}
        onClose={() => setDeleteDialog((prev) => ({ ...prev, open: false }))}
        onSubmit={() => burnTroop(deleteDialog.id)}
        reward={deleteDialog.reward}
        loading={barracksUIProps?.upgradeLoading}
      />
    </>
  );
};

export default BarracksPage;
